import { computed, observable, subscribe } from 'knockout-decorators'

import { CartPrecheckStatusEnglish, CheckResult } from '@tixa/schema'

export class ReservationState {
  private seatmapId = ''

  constructor(params: { seatmapId: string }) {
    this.seatmapId = params.seatmapId
  }

  @observable
  seatmap: any = null

  @observable
  seatmapReservations: any = null

  @observable
  seatmapValidationError: string | null = null

  @observable
  isSeatmapOpen = false

  @computed
  get isAvailable(): boolean {
    return (
      this.seatmap?.itemContainer.activeSeatCount > 0 ||
      this.seatmap?.tickets.isAnyStandingTicketAvailable()
    )
  }

  @computed get submit(): {
    id: string
    action: () => Promise<CheckResult>
    isEnabled: boolean
  } {
    return {
      action: () => this.submitAction(),
      id: this.seatmapId,
      isEnabled: this.isSeatmapOpen,
    }
  }

  private submitAction(): Promise<CheckResult> {
    // Only reset error, not reservations (to avoid triggering subscription with null)
    this.seatmapValidationError = null

    return new Promise<CheckResult>((resolve, reject) => {
      let errorSubscription: any = null
      let reservationSubscription: any = null
      let timeoutId: any = null
      let isResolved = false

      // Capture the current reservation value to detect new changes
      const previousReservation = this.seatmapReservations

      const cleanup = () => {
        if (errorSubscription) {
          errorSubscription.dispose()
        }
        if (reservationSubscription) {
          reservationSubscription.dispose()
        }
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      }

      // Add timeout to prevent hanging forever
      timeoutId = setTimeout(() => {
        if (!isResolved) {
          isResolved = true
          cleanup()
          reject({
            status: CartPrecheckStatusEnglish.error,
            isCheckSuccess: false,
            error: 'Timeout waiting for seat reservation',
          })
        }
      }, 30000)

      errorSubscription = subscribe<string | null>(
        () => this.seatmapValidationError,
        (error) => {
          if (error && !isResolved) {
            isResolved = true
            this.seatmapValidationError = null
            cleanup()
            // Reject the promise so the UI can handle the error
            reject({
              status: CartPrecheckStatusEnglish.error,
              isCheckSuccess: false,
              error: error,
            })
          }
        }
      )

      // Subscribe to new reservations
      // Reservations are added on seatmap config callback
      reservationSubscription = subscribe<any>(
        () => this.seatmapReservations,
        // When there is a value, resolve the promise
        (reservation) => {
          // Only resolve if this is a NEW reservation (not the same as before)
          // and the reservation has valid data
          if (
            !isResolved &&
            reservation &&
            reservation !== previousReservation
          ) {
            isResolved = true
            cleanup()
            resolve({
              status: CartPrecheckStatusEnglish.success,
              isCheckSuccess: true,
              reservation,
            })
          }
        }
      )
      // Trigger seatmap send
      this.seatmap.sendTickets()
    })
  }
}
